/**
 * Push Providers Config — Super Admin only.
 * Lets the platform owner switch between Expo Push (default, free),
 * Firebase Cloud Messaging (Android), and Apple Push Notifications (iOS).
 * Secret fields are write-only: GET returns booleans, PUT accepts the value,
 * DELETE clears it.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Cloud, Server, Smartphone, Apple, ShieldCheck, KeyRound, Trash2, Loader2, CheckCircle2, AlertTriangle, Info } from "lucide-react";

const PROVIDER_META = {
    expo: { label: "Expo Push", icon: Cloud,
        sub: "Default · relays via FCM + APNs via Expo's infra. No credentials needed.", color: "primary" },
    fcm: { label: "Firebase Cloud Messaging", icon: Server,
        sub: "Direct Google FCM (v1 HTTP API). Best for Android volume + reliability.", color: "primary" },
    apns: { label: "Apple Push Notifications", icon: Apple,
        sub: "Direct APNs over HTTP/2 + .p8 auth key. Required for iOS critical alerts.", color: "primary" },
};

function FieldLabel({ children, secret, isSet, onClear }) {
    return (
        <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{children}</span>
            {secret && isSet && (
                <button type="button" onClick={onClear} className="text-[10px] text-rose-400 hover:text-rose-300 inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3"/>Clear saved
                </button>
            )}
            {secret && (
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${isSet ? "border-emerald-500/40 text-emerald-400" : "border-border text-muted-foreground"}`}>
                    {isSet ? "SAVED" : "EMPTY"}
                </span>
            )}
        </div>
    );
}

export default function PushProvidersConfig() {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [validating, setValidating] = useState(false);
    const [validationResult, setValidationResult] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/push/providers");
            setCfg(data);
            // Hydrate form with non-secret fields; leave secrets blank
            setForm({
                active_provider: data.active_provider,
                ios_provider: data.ios_provider,
                android_provider: data.android_provider,
                fcm_project_id: data.fcm_project_id || "",
                apns_key_id: data.apns_key_id || "",
                apns_team_id: data.apns_team_id || "",
                apns_bundle_id: data.apns_bundle_id || "",
                test_mode: data.test_mode ?? true,
            });
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form };
            // Only include secrets if user typed something
            if (form.fcm_service_account_json?.trim()) payload.fcm_service_account_json = form.fcm_service_account_json;
            if (form.apns_p8_key?.trim()) payload.apns_p8_key = form.apns_p8_key;
            const { data } = await api.put("/admin/push/providers", payload);
            setCfg(data);
            setForm(f => ({ ...f, fcm_service_account_json: "", apns_p8_key: "" }));
            toast.success("Push provider settings saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const clearSecret = async (field) => {
        if (!window.confirm(`Clear saved ${field}?`)) return;
        try {
            await api.delete(`/admin/push/providers/secret/${field}`);
            toast.success("Cleared");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const validate = async () => {
        setValidating(true);
        try {
            const { data } = await api.post("/admin/push/providers/validate");
            setValidationResult(data);
            toast.success("Validation complete");
        } catch (e) { toast.error(apiError(e)); }
        finally { setValidating(false); }
    };

    if (loading) return <div className="text-muted-foreground text-sm">Loading providers…</div>;
    if (!cfg) return null;

    return (
        <div className="space-y-6" data-testid="admin-push-providers">
            <div>
                <h2 className="text-foreground text-lg">Push providers</h2>
                <p className="text-muted-foreground text-[12px] mt-1">
                    Switch between Expo (default), Firebase Cloud Messaging (Android), and Apple Push Notifications (iOS). Credentials are stored encrypted at rest.
                </p>
            </div>

            {/* Provider picker */}
            <div className="grid sm:grid-cols-3 gap-3" data-testid="provider-picker">
                {Object.entries(PROVIDER_META).map(([key, meta]) => {
                    const Icon = meta.icon;
                    const active = form.active_provider === key;
                    return (
                        <button key={key} onClick={() => setForm({ ...form, active_provider: key })}
                            data-testid={`provider-${key}`}
                            className={`text-left p-4 border ${active ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}>
                            <Icon className={`w-5 h-5 mb-2 ${active ? "text-primary" : "text-muted-foreground"}`}/>
                            <div className="text-foreground text-sm">{meta.label}</div>
                            <div className="text-muted-foreground text-[11px] mt-1">{meta.sub}</div>
                            {active && <div className="mt-2 text-[10px] uppercase tracking-[0.16em] text-primary">Active</div>}
                        </button>
                    );
                })}
            </div>

            {/* Per-platform override */}
            <div className="grid sm:grid-cols-2 gap-3">
                <div className="border border-border bg-card p-4">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2 flex items-center gap-1">
                        <Smartphone className="w-3 h-3"/>iOS provider override
                    </div>
                    <select value={form.ios_provider || "expo"} onChange={e => setForm({ ...form, ios_provider: e.target.value })}
                        data-testid="provider-ios"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm">
                        <option value="expo">Expo (default)</option>
                        <option value="apns">APNs (direct)</option>
                    </select>
                </div>
                <div className="border border-border bg-card p-4">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2 flex items-center gap-1">
                        <Smartphone className="w-3 h-3"/>Android provider override
                    </div>
                    <select value={form.android_provider || "expo"} onChange={e => setForm({ ...form, android_provider: e.target.value })}
                        data-testid="provider-android"
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm">
                        <option value="expo">Expo (default)</option>
                        <option value="fcm">Firebase FCM (direct)</option>
                    </select>
                </div>
            </div>

            {/* Firebase FCM credentials */}
            <details className="border border-border bg-card p-4" open={cfg.fcm_service_account_json_set || form.android_provider === "fcm"}>
                <summary className="cursor-pointer text-foreground text-sm flex items-center gap-2">
                    <Server className="w-4 h-4 text-primary"/>Firebase Cloud Messaging (Android)
                    <span className="text-muted-foreground text-[11px] ml-2">
                        Get from Firebase Console → Project Settings → Service Accounts → Generate new private key
                    </span>
                </summary>
                <div className="mt-4 space-y-4">
                    <div>
                        <FieldLabel>Project ID (auto-filled if you paste the JSON below)</FieldLabel>
                        <input value={form.fcm_project_id || ""} onChange={e => setForm({ ...form, fcm_project_id: e.target.value })}
                            placeholder="lawos-india"
                            data-testid="fcm-project-id"
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm font-mono"/>
                    </div>
                    <div>
                        <FieldLabel secret isSet={cfg.fcm_service_account_json_set}
                            onClear={() => clearSecret("fcm_service_account_json")}>
                            Service-account JSON (paste full JSON)
                        </FieldLabel>
                        <textarea value={form.fcm_service_account_json || ""} onChange={e => setForm({ ...form, fcm_service_account_json: e.target.value })}
                            placeholder={'{"type":"service_account","project_id":"…","private_key_id":"…","private_key":"-----BEGIN PRIVATE KEY-----\\n…\\n-----END PRIVATE KEY-----","client_email":"…","client_id":"…","auth_uri":"…","token_uri":"…",…}'}
                            data-testid="fcm-service-account"
                            rows={5}
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-[11px] font-mono"/>
                        <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                            <Info className="w-3 h-3"/>Stored encrypted at rest · never returned to the browser
                        </div>
                    </div>
                </div>
            </details>

            {/* APNs credentials */}
            <details className="border border-border bg-card p-4" open={cfg.apns_p8_key_set || form.ios_provider === "apns"}>
                <summary className="cursor-pointer text-foreground text-sm flex items-center gap-2">
                    <Apple className="w-4 h-4 text-primary"/>Apple Push Notifications (iOS)
                    <span className="text-muted-foreground text-[11px] ml-2">
                        Get from Apple Developer → Keys → "+ Create Key" with APNs scope · download .p8
                    </span>
                </summary>
                <div className="mt-4 space-y-4">
                    <div className="grid sm:grid-cols-3 gap-3">
                        <div>
                            <FieldLabel>Key ID</FieldLabel>
                            <input value={form.apns_key_id || ""} onChange={e => setForm({ ...form, apns_key_id: e.target.value })}
                                placeholder="ABC123DEFG"
                                data-testid="apns-key-id"
                                className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm font-mono"/>
                        </div>
                        <div>
                            <FieldLabel>Team ID</FieldLabel>
                            <input value={form.apns_team_id || ""} onChange={e => setForm({ ...form, apns_team_id: e.target.value })}
                                placeholder="1A2B3C4D5E"
                                data-testid="apns-team-id"
                                className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm font-mono"/>
                        </div>
                        <div>
                            <FieldLabel>Bundle ID</FieldLabel>
                            <input value={form.apns_bundle_id || ""} onChange={e => setForm({ ...form, apns_bundle_id: e.target.value })}
                                placeholder="in.lawos.mobile"
                                data-testid="apns-bundle-id"
                                className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm font-mono"/>
                        </div>
                    </div>
                    <div>
                        <FieldLabel secret isSet={cfg.apns_p8_key_set} onClear={() => clearSecret("apns_p8_key")}>
                            AuthKey .p8 (paste file content incl. BEGIN/END lines)
                        </FieldLabel>
                        <textarea value={form.apns_p8_key || ""} onChange={e => setForm({ ...form, apns_p8_key: e.target.value })}
                            placeholder={"-----BEGIN PRIVATE KEY-----\nMIGTAg…\n-----END PRIVATE KEY-----"}
                            data-testid="apns-p8-key"
                            rows={5}
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-[11px] font-mono"/>
                    </div>
                </div>
            </details>

            <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="accent-primary"
                    checked={!!form.test_mode} onChange={e => setForm({ ...form, test_mode: e.target.checked })}
                    data-testid="push-test-mode"/>
                <span className="text-foreground text-xs">
                    Test mode — pushes go to Expo's sandbox / APNs development env (until you flip this off for production)
                </span>
            </label>

            <div className="flex items-center gap-2 flex-wrap justify-end pt-3 border-t border-border">
                <button onClick={validate} disabled={validating}
                    data-testid="providers-validate"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border text-muted-foreground hover:text-primary hover:border-primary text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                    {validating ? <Loader2 className="w-3 h-3 animate-spin"/> : <ShieldCheck className="w-3 h-3"/>}Validate
                </button>
                <button onClick={save} disabled={saving}
                    data-testid="providers-save"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                    {saving ? <Loader2 className="w-3 h-3 animate-spin"/> : <KeyRound className="w-3 h-3"/>}Save
                </button>
            </div>

            {validationResult && (
                <div className="border border-border bg-card p-3" data-testid="providers-validation-result">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Validation result</div>
                    <div className="grid sm:grid-cols-3 gap-2 mb-2">
                        {["expo", "fcm", "apns"].map(p => (
                            <div key={p} className="flex items-center gap-2 text-xs">
                                {validationResult[p] ? <CheckCircle2 className="w-4 h-4 text-emerald-400"/> : <AlertTriangle className="w-4 h-4 text-muted-foreground"/>}
                                <span className="capitalize text-foreground">{p}</span>
                                <span className="text-muted-foreground">{validationResult[p] ? "ready" : "not configured"}</span>
                            </div>
                        ))}
                    </div>
                    {(validationResult.issues || []).length > 0 && (
                        <div className="text-[11px] text-rose-400 space-y-1 mt-2 border-t border-border pt-2">
                            {validationResult.issues.map((iss, i) => <div key={i}>· {iss}</div>)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
